import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GridColDef, GridSortModel } from "@mui/x-data-grid"
import { useTranslation } from "react-i18next"

import { RiSparklingLine } from "@remixicon/react"

import SectionContainer from "@/app/report/components/SectionContainer"
import SectionMenu from "@/app/report/components/SectionMenu"
import Dialog from "@/components/dialog/Dialog"
import { generateAiUserPromptReport } from "@/app/api/aiReport"
import { createGlobalStore } from "@/stores/global"
import ColumnFilterPopover from "@/app/report/components/section/ColumnFilterPopover"
import ReportSectionSessionSelector from "@/app/report/components/section/ReportSectionSessionSelector"
import { useSearchParams } from "@/hooks/useSearchParams"
import { getTextWidth } from "@/app/report/common/tools"
import { DEFAULT_CSV_OPTIONS, REPORT_TYPE } from "@/app/report/common/constants"
import { StatsSettings } from "@/app/report/common/type"
import Table from "@/components/table/Table"
import { overflowPagination } from "@/components/table/Pagination"
import BasicButton from "@/components/button/BasicButton"
import Icon from "@/components/icon/Icon"
import { convertUnitSquare, localeStringToNumber } from "@/utils/helper"
import { getThresholdCellStyle } from "@/utils/threshold"
import { loadThresholds } from "@/utils/thresholdStorage"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePositionRank, usePositionGroupRank } from "@/hooks/usePositionRank"
import { exportTableFromView } from "@/app/report/common/export/exportTable"
import { createAccountStore } from "@/stores/account"
import { colors } from "@/components/colors"
import {
  applyColumnFilters,
  buildCustomTableSummaryRows,
  countLeadingFixedColumns,
  createCompareByGroupKey,
  mapSummaryRowsToGridRows,
  mapTableBodyToRows,
  resolveSummaryRowKinds,
  sortCustomTableRows,
  useCustomTableData,
} from "@/app/report/hooks/useCustomTableData"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import {
  ColumnFilterMap,
  createReportColumnFilterStore,
  hasActiveColumnFilters,
} from "@/stores/reportColumnFilter"
import {
  createReportSessionSelectionStore,
  resolveSectionSessionIds,
} from "@/stores/reportSessionSelection"
import {
  buildPersistedSortKey,
  loadCustomTableSort,
  saveCustomTableSort,
} from "@/utils/reportSortStorage"
import { Session } from "@/types/session"

import "./statsByPlayerSection.scss"

const EMPTY_FILTERS: ColumnFilterMap = {}

// Custom Table 데이터(헤더/바디) 구성은 화면·PDF 공용 훅으로 이동 — useCustomTableData 참조.
// GROUP_KEY_MAPPING 은 기존 외부 사용처 호환을 위해 re-export.
export { GROUP_KEY_MAPPING } from "@/app/report/hooks/useCustomTableData"

export default function CustomTableSection({
  settings,
  reportType,
  sectionIndex,
  onSortChange,
  sessionMode = false,
  sessionList = [],
  reportSessionIds = [],
}: {
  settings: StatsSettings
  reportType: REPORT_TYPE
  sectionIndex: number
  onSortChange?: (sortSetting: { key: string; order: "asc" | "desc" } | undefined) => void
  // Daily 세션 모드(#99-1): 리포트 필터에서 고른 세션 안에서 이 표만의 세션을 다시 고를 수 있다 (Stats by Player 와 동일)
  sessionMode?: boolean
  sessionList?: Session[]
  reportSessionIds?: number[]
}) {
  const { t } = useTranslation()
  const tableRef = useRef<HTMLDivElement>(null)
  const searchParams = useSearchParams()
  const [sessionSelectorOpen, setSessionSelectorOpen] = useState(false)

  // 프로토타입: API 미반영 시 localStorage fallback
  const thresholds = useMemo(
    () => settings.thresholds ?? loadThresholds(settings.title),
    [settings.thresholds, settings.title],
  )

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null)
  const [filterPosition, setFilterPosition] = useState<{ top: number; left: number } | undefined>(
    undefined,
  )
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null)

  // 화면 정렬은 리포트 스코프 store 에 보존(템플릿 미저장) — PDF export 와 공유 (WEB-2274)
  const storeKey = buildReportSortKey(reportType, sectionIndex, 0)
  const storedSort = createReportSortStore((state) => state.sorts[storeKey])
  const setSort = createReportSortStore((state) => state.setSort)

  // 정렬은 브라우저에 보존(#99-6, 서버 저장 미지원 WEB-2193) — 새로고침·재진입 시 transient store 에 seed
  const persistedSortKey = buildPersistedSortKey(
    reportType,
    searchParams.get("idfTemplate"),
    sectionIndex,
  )
  const hasStoredSort = storedSort !== undefined
  useEffect(() => {
    if (hasStoredSort) {
      return
    }
    const persisted = loadCustomTableSort(persistedSortKey)
    if (persisted) {
      setSort(storeKey, persisted)
    }
  }, [hasStoredSort, persistedSortKey, storeKey, setSort])

  // 섹션 내 세션 선택도 같은 스코프 store — PDF 가 같은 세션 기준으로 집계 (#99-1)
  const storedSessionSelection = createReportSessionSelectionStore(
    (state) => state.selections[storeKey],
  )
  const setSessionSelection = createReportSessionSelectionStore((state) => state.setSelection)
  const selectedSessionIds = useMemo(
    () => resolveSectionSessionIds(reportSessionIds, storedSessionSelection),
    [reportSessionIds, storedSessionSelection],
  )
  const selectableSessionList = useMemo(
    () => sessionList.filter((session) => reportSessionIds.includes(session.idfSession)),
    [sessionList, reportSessionIds],
  )

  // 컬럼 범위 필터도 같은 스코프 store — PDF 가 화면과 같은 행·요약 값을 내도록 (#98 WYSIWYG)
  const storedFilters = createReportColumnFilterStore((state) => state.filters[storeKey])
  const setStoredFilters = createReportColumnFilterStore((state) => state.setFilters)
  const columnFilters = storedFilters ?? EMPTY_FILTERS
  const setColumnFilters = useCallback(
    (update: ColumnFilterMap | ((prev: ColumnFilterMap) => ColumnFilterMap)) => {
      const prev = createReportColumnFilterStore.getState().filters[storeKey] ?? EMPTY_FILTERS
      setStoredFilters(storeKey, typeof update === "function" ? update(prev) : update)
    },
    [storeKey, setStoredFilters],
  )
  const sortModel: GridSortModel = useMemo(
    () => (storedSort ? [{ field: storedSort.key, sort: storedSort.order }] : []),
    [storedSort],
  )

  const [aiDialogOpen, setAiDialogOpen] = useState(false)
  const [aiTitle, setAiTitle] = useState("")
  const [aiUserPrompt, setAiUserPrompt] = useState("")
  const [aiLoading, setAiLoading] = useState(false)

  const translateKey = useTranslateMetricKey()
  const positionRank = usePositionRank()
  const positionGroupRank = usePositionGroupRank()
  const { teamProfileInfo } = createAccountStore()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const {
    tableHeader,
    tableBody,
    rowSources,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    displayColumnCount,
    isLoading: groupSummaryLoading,
  } = useCustomTableData(settings, {
    reportType,
    sessionMode,
    sessionIds: sessionMode ? selectedSessionIds : undefined,
  })

  const pinnedColumns = useMemo(() => {
    const fixedColumnKeys = tableHeader
      .filter((header) => header.isFixed)
      .map((header) => header.key)

    return { left: fixedColumnKeys }
  }, [tableHeader])

  const columnMinMax = useMemo(() => {
    const result: Record<string, { min: number; max: number }> = {}

    tableHeader.forEach((header, headerIndex) => {
      if (!header.unit) {
        return
      }

      let min = Infinity
      let max = -Infinity

      tableBody.forEach((row) => {
        const cellValue = row[headerIndex]
        if (cellValue === null || cellValue === undefined || cellValue === "") {
          return
        }

        let numValue: number
        if (typeof cellValue === "number") {
          numValue = cellValue
        } else {
          numValue = localeStringToNumber(String(cellValue))
        }

        if (!isNaN(numValue)) {
          min = Math.min(min, numValue)
          max = Math.max(max, numValue)
        }
      })

      if (min !== Infinity && max !== -Infinity) {
        result[header.key] = { min: 0, max }
      }
    })

    return result
  }, [tableHeader, tableBody])

  const handleFilterClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, columnKey: string) => {
      event.stopPropagation()
      event.preventDefault()

      const columnHeader = event.currentTarget.closest(".MuiDataGrid-columnHeader") as HTMLElement
      if (columnHeader) {
        const rect = columnHeader.getBoundingClientRect()
        setFilterPosition({ top: rect.bottom, left: rect.left })
      }

      setFilterAnchorEl(columnHeader || event.currentTarget)
      setActiveFilterColumn(columnKey)

      if (!columnFilters[columnKey] && columnMinMax[columnKey]) {
        const { min, max } = columnMinMax[columnKey]
        setColumnFilters((prev) => ({
          ...prev,
          [columnKey]: { min, max, range: [min, max] },
        }))
      }
    },
    [columnFilters, columnMinMax],
  )

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
    setFilterPosition(undefined)
    setActiveFilterColumn(null)
  }

  const handleFilterApply = (columnKey: string, range: [number, number]) => {
    const minMax = columnMinMax[columnKey]
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: {
        min: minMax?.min ?? 0,
        max: minMax?.max ?? 0,
        range,
      },
    }))
  }

  const columns: GridColDef[] = useMemo(() => {
    const getHeaderWidth = (header: string) => {
      switch (t(header, { lng: "en" })) {
        case "Date":
          return 100
        case "Activity Name":
          return 300
        default:
          return getTextWidth(t(header))
      }
    }

    // 요약 행 라벨(Total/Average)은 첫 열부터 연속된 고정 컬럼을 하나로 병합해 표시
    const summaryLabelColSpan = Math.max(1, countLeadingFixedColumns(tableHeader))

    return tableHeader.map((header, headerIndex) => {
      const hasFilter = !!header.unit && !!columnMinMax[header.key]
      const isFiltered =
        columnFilters[header.key] &&
        (columnFilters[header.key].range[0] !== columnFilters[header.key].min ||
          columnFilters[header.key].range[1] !== columnFilters[header.key].max)

      return {
        field: header.key,
        ...(headerIndex === 0 && summaryLabelColSpan > 1
          ? {
              colSpan: (_value: unknown, row: { isSummary?: boolean }) =>
                row?.isSummary ? summaryLabelColSpan : 1,
            }
          : {}),
        headerClassName: hasFilter ? "custom-table-filterable-header" : "",
        renderHeader: () => {
          // title 키 기준 — 선수 이름 컬럼은 Stats 와 같은 "Player Name" (#99-4)
          const newHeaderTitle = translateKey(header.title)

          return (
            <>
              <div className="custom-table-header-content">
                <span>{newHeaderTitle}</span>
                {header.unit && (
                  <span className="report-stats-table-header-unit">
                    {convertUnitSquare(header.unit)}
                  </span>
                )}
              </div>
              {hasFilter && (
                <button
                  className={`custom-table-filter-btn ${isFiltered ? "active" : ""}`}
                  onClick={(e) => handleFilterClick(e, header.key)}>
                  <Icon type="filter" size={14} color={colors.neutral.grey[500]} />
                </button>
              )}
            </>
          )
        },
        unit: !!header.unit,
        renderCell: (params) => {
          // 요약(Total/Average) 행: 표시 컬럼(그룹·시간·건수)은 라벨/빈칸 그대로,
          // 메트릭 셀(RPE·ACD Load 같은 단위 없는 지표 포함)은 빈값 "-" 표기·임계값 하이라이트 미적용
          if (params.row.isSummary) {
            if (headerIndex < displayColumnCount) {
              return <div>{params.value}</div>
            }

            return (
              <div>
                {params.value !== null && params.value !== undefined && params.value !== ""
                  ? params.value
                  : "-"}
              </div>
            )
          }

          if (params.field === "activityType") {
            return <div>{t(params.value)}</div>
          }

          // 임계값 기반 셀 하이라이트 적용
          const threshold = thresholds?.find((th) => th.metricKey === header.key)
          const thresholdStyle = threshold ? getThresholdCellStyle(params.value, threshold) : null

          const displayValue =
            params.value !== null && params.value !== undefined && params.value !== ""
              ? params.value
              : "-"

          return thresholdStyle ? (
            <span
              style={{
                backgroundColor: thresholdStyle.backgroundColor,
                color: thresholdStyle.color,
                padding: "2px 10px",
                borderRadius: "6px",
                fontSize: "12px",
                lineHeight: "20px",
              }}>
              {displayValue}
            </span>
          ) : (
            <div>{displayValue}</div>
          )
        },
        width:
          getHeaderWidth(translateKey(header.title) ?? t(header.title)) + (header.unit ? 50 : 0),
      }
    })
  }, [tableHeader, translateKey, t, columnMinMax, columnFilters, handleFilterClick, thresholds])

  const compareByGroupKey = useMemo(
    () => createCompareByGroupKey(positionRank, positionGroupRank),
    [positionRank, positionGroupRank],
  )

  const rows = useMemo(() => {
    return sortCustomTableRows(
      mapTableBodyToRows(tableBody, tableHeader),
      storedSort,
      settings.groupKey1,
      settings.groupKey2,
      compareByGroupKey,
    )
  }, [
    tableBody,
    tableHeader,
    storedSort,
    settings.groupKey1,
    settings.groupKey2,
    compareByGroupKey,
  ])

  const filteredRows = useMemo(() => applyColumnFilters(rows, columnFilters), [rows, columnFilters])

  // 템플릿 설정(Summary Row 체크박스)에서 켠 요약 행만 — 필드 없는 기존 표는 미표시 (#155)
  const summaryRowKinds = useMemo(
    () => resolveSummaryRowKinds(settings.summaryRows),
    [settings.summaryRows],
  )

  // Total / Average 요약 행 — 화면에 남은 행(필터 적용 후)의 원본 Summary 로 집계, 정렬과 무관하게 상단 고정 (#98)
  const summaryGridRows = useMemo(() => {
    if (!filteredRows.length || !summaryRowKinds.length) {
      return []
    }
    const sources = filteredRows
      .map((row) => rowSources[row._rowIndex as number])
      .filter((source): source is (typeof rowSources)[number] => !!source)
    const summaryRows = buildCustomTableSummaryRows({
      tableHeader,
      displayColumnCount,
      rowSources: sources,
      metricMap,
      selectedUnitList,
      calculatedMetrics,
      t,
      kinds: summaryRowKinds,
    })

    return mapSummaryRowsToGridRows(summaryRows, tableHeader, summaryRowKinds)
  }, [
    filteredRows,
    rowSources,
    tableHeader,
    displayColumnCount,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
    summaryRowKinds,
    t,
  ])

  const gridRows = useMemo(
    () => [...summaryGridRows, ...filteredRows] as Array<Record<string, number | string | boolean>>,
    [summaryGridRows, filteredRows],
  )

  useEffect(() => {
    const gridElements = document?.querySelectorAll(".MuiDataGrid-virtualScroller")
    if (!gridElements) {
      return
    }
    const listeners: Array<() => void> = []
    gridElements.forEach((gridElement) => {
      const onScroll = () => {
        const maxScrollLeft = gridElement.scrollWidth - gridElement.clientWidth - 31
        if (gridElement.scrollLeft >= maxScrollLeft) {
          gridElement.scrollLeft = maxScrollLeft
        }
      }

      gridElement.addEventListener("scroll", onScroll, { passive: false })
      listeners.push(() => gridElement.removeEventListener("scroll", onScroll))
    })

    return () => {
      listeners.forEach((removeListener) => removeListener())
    }
  }, [columns])

  const exportTable = () => {
    const reportTypeLabel = reportType === REPORT_TYPE.DAILY ? "Daily" : "Trend"
    const fileName = `${reportTypeLabel}_${teamProfileInfo?.name || ""}_${settings.title}`

    const headers = tableHeader.map((header) => ({
      key: header.key,
      title: translateKey(header.title) || t(header.title),
      unit: header.unit,
    }))

    // 화면과 동일: 요약 2행(상단) + 필터 적용된 행
    exportTableFromView({
      fileName,
      headers,
      rows: gridRows as unknown as Record<string, string | number | null>[],
    })
  }

  const hasActiveFilters = hasActiveColumnFilters(columnFilters)

  const handleResetAllFilters = () => {
    setColumnFilters((prev) => {
      const reset: ColumnFilterMap = {}
      Object.keys(prev).forEach((key) => {
        const filter = prev[key]
        reset[key] = { ...filter, range: [filter.min, filter.max] }
      })

      return reset
    })
  }

  const buildCsvData = (): string => {
    const escapeCsv = (v: string) =>
      v.includes(",") || v.includes('"') || v.includes("\n") ? `"${v.replace(/"/g, '""')}"` : v

    const headerRow = tableHeader
      .map((h) => escapeCsv(translateKey(h.title) || t(h.title)))
      .join(",")
    const dataRows = filteredRows.map((row) =>
      tableHeader.map((h) => escapeCsv(String(row[h.key] ?? "-"))).join(","),
    )

    return [headerRow, ...dataRows].join("\n")
  }

  const handleGenerateAiReport = async () => {
    setAiLoading(true)
    try {
      const csvData = buildCsvData()
      await generateAiUserPromptReport({
        title: aiTitle,
        userPrompt: aiUserPrompt,
        csvData,
      })
      setSnackBar({
        open: true,
        message: t("aiAnalysisSuccess", "AI analysis report has been generated."),
        severity: "success",
      })
      setAiDialogOpen(false)
      setAiTitle("")
      setAiUserPrompt("")
    } catch {
      setSnackBar({
        open: true,
        message: t("aiAnalysisFailed", "Failed to generate AI analysis report."),
        severity: "error",
      })
    } finally {
      setAiLoading(false)
    }
  }

  const handleAiDialogClose = () => {
    if (aiLoading) return
    setAiDialogOpen(false)
    setAiTitle("")
    setAiUserPrompt("")
  }

  return (
    <>
      <SectionContainer
        title={settings.title}
        sectionMenu={
          <div className="ReportStatsSectionMenuCont">
            {sessionMode && selectableSessionList.length > 0 && (
              <ReportSectionSessionSelector
                open={sessionSelectorOpen}
                setOpen={setSessionSelectorOpen}
                sessionList={selectableSessionList}
                selectedSessionList={selectedSessionIds}
                setSelectedSessionList={(value) => setSessionSelection(storeKey, value)}
              />
            )}
            {hasActiveFilters && (
              <BasicButton type="default-outlined" size="sm" onClick={handleResetAllFilters}>
                <Icon type="refresh" size={16} color={colors.neutral.grey[700]} />
                {t("button.reset")}
              </BasicButton>
            )}
            {teamProfileInfo?.teamPermissions?.ACCESS_AI_REPORT && (
              <BasicButton type="default-outlined" size="sm" onClick={() => setAiDialogOpen(true)}>
                <RiSparklingLine size={16} />
                {t("aiAnalysis", "AI Analysis")}
              </BasicButton>
            )}
            <SectionMenu
              menuItems={DEFAULT_CSV_OPTIONS.map((option) => ({
                ...option,
                func: exportTable,
              }))}
            />
          </div>
        }
        loading={groupSummaryLoading}>
        <div className="ReportStatsTableCont CustomTableSectionCont">
          <Table
            ref={tableRef}
            columns={columns}
            rows={gridRows}
            getRowId={(row) => `row-${row._rowIndex}`}
            oddRowColor
            hasSummaryRow={summaryGridRows.length > 0}
            fillLastColumn
            disableVirtualization
            disableColumnMenu={true}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(model) => {
              const newSort =
                model.length > 0
                  ? { key: model[0].field, order: model[0].sort as "asc" | "desc" }
                  : undefined
              saveCustomTableSort(persistedSortKey, newSort)
              setSort(storeKey, newSort)
              if (onSortChange) {
                onSortChange(newSort)
              }
            }}
            slots={{
              pagination: overflowPagination(gridRows.length),
            }}
            pinnedColumns={pinnedColumns}
          />
        </div>
      </SectionContainer>
      <Dialog
        open={aiDialogOpen}
        title={t("aiAnalysis", "AI Analysis")}
        onClose={handleAiDialogClose}
        onConfirm={handleGenerateAiReport}
        footer={
          <div className="AiReportDialog__footer">
            <BasicButton type="default-outlined" size="md" onClick={handleAiDialogClose}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton
              type="primary-contained"
              size="md"
              onClick={handleGenerateAiReport}
              disabled={aiLoading || !aiTitle.trim() || !aiUserPrompt.trim()}
              isLoading={aiLoading}>
              {t("generate", "Generate")}
            </BasicButton>
          </div>
        }>
        <div className="AiReportDialog">
          <div className="AiReportDialog__field">
            <label className="AiReportDialog__label">{t("title")}</label>
            <input
              className="AiReportDialog__input"
              type="text"
              value={aiTitle}
              onChange={(e) => setAiTitle(e.target.value)}
              placeholder={t("aiTitlePlaceholder", "Enter report title")}
              disabled={aiLoading}
            />
          </div>
          <div className="AiReportDialog__field">
            <label className="AiReportDialog__label">{t("userPrompt", "User Prompt")}</label>
            <textarea
              className="AiReportDialog__textarea"
              value={aiUserPrompt}
              onChange={(e) => setAiUserPrompt(e.target.value)}
              placeholder={t("aiPromptPlaceholder", "Describe what you want to analyze...")}
              disabled={aiLoading}
            />
          </div>
        </div>
      </Dialog>
      {activeFilterColumn && columnMinMax[activeFilterColumn] && (
        <ColumnFilterPopover
          open={!!filterAnchorEl}
          anchorPosition={filterPosition}
          onClose={handleFilterClose}
          columnName={
            translateKey(tableHeader.find((h) => h.key === activeFilterColumn)?.title ?? "") ||
            activeFilterColumn
          }
          unit={tableHeader.find((h) => h.key === activeFilterColumn)?.unit || null}
          minValue={columnMinMax[activeFilterColumn].min}
          maxValue={columnMinMax[activeFilterColumn].max}
          currentRange={
            columnFilters[activeFilterColumn]?.range || [
              columnMinMax[activeFilterColumn].min,
              columnMinMax[activeFilterColumn].max,
            ]
          }
          onApply={(range) => handleFilterApply(activeFilterColumn, range)}
        />
      )}
    </>
  )
}
